import React, { useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, TextField, Button, Paper, Typography, Chip, CircularProgress,
  List, ListItem, ListItemText, Divider, Grid, Accordion,
  AccordionSummary, AccordionDetails, Checkbox, Avatar, FormControl, InputLabel, Select,
  MenuItem, Autocomplete, ToggleButton, ToggleButtonGroup, Collapse,
  Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';
import SortIcon from '@mui/icons-material/Sort';
import { searchParliamentarians } from '../services/api';
import YearRangeSlider from './YearRangeSlider';
import { getParliamentarianPhotoUrl } from '../utils/photos';
import { formatParliamentarianName } from '../utils/formatName';
import SearchSkeleton from './SearchSkeleton';
import EmptyState from './EmptyState';
import ErrorState from './ErrorState';
import PageSizeSelector, { DEFAULT_PAGE_SIZE } from './PageSizeSelector';
import ExportCSVButton from './ExportCSVButton';
import { SaveSearchButton } from './saved-searches';

const currentYear = new Date().getFullYear();

export default function ParliamentariansSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchMode, setSearchMode] = useState(searchParams.get('mode') || 'simple');
  const [query, setQuery] = useState(searchParams.get('q') || '');

  // Advanced form state
  const [form, setForm] = useState({
    keywords: '',
    name: '',
    party: '',
    state: '',
    electorate: '',
    gender: '',
    chamber: '',
    inCurrentParliament: 'all',
    firstNations: 'all'
  });
  const [birthYearRange, setBirthYearRange] = useState([1850, currentYear]);
  const [serviceYearRange, setServiceYearRange] = useState([1901, currentYear]);

  // Shared state
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilters, setActiveFilters] = useState({ InCurrentParliament: ['True'] });
  const [computedFacets, setComputedFacets] = useState({});
  const [sortOrder, setSortOrder] = useState('service_asc'); // Default to service start (oldest first)
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const urlPageSize = parseInt(searchParams.get('pageSize'), 10);
    return [10, 25, 50, 100].includes(urlPageSize) ? urlPageSize : DEFAULT_PAGE_SIZE;
  });
  // All Australian states and territories (hardcoded to ensure completeness)
  const ALL_STATES = [
    'Australian Capital Territory',
    'New South Wales',
    'Northern Territory',
    'Queensland',
    'South Australia',
    'Tasmania',
    'Victoria',
    'Western Australia'
  ];

  const [availableOptions, setAvailableOptions] = useState({
    names: [],
    parties: [],
    states: ALL_STATES,
    electorates: [],
    genders: ['Male', 'Female'],
    chambers: ['Member', 'Senator']
  });

  // Server-side name search state
  const [nameSearchOptions, setNameSearchOptions] = useState([]);
  const [nameSearchLoading, setNameSearchLoading] = useState(false);
  const nameSearchTimeoutRef = React.useRef(null);

  // Fetch autocomplete options on mount - use larger limit for comprehensive lists
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        // Fetch more records to get better options (backend caps per_page at 100)
        const data = await searchParliamentarians('*', 1, 100);
        if (data.hits) {
          const names = [...new Set(data.hits.map(h => h.DisplayName).filter(Boolean))].sort();
          const parties = [...new Set(data.hits.map(h => h.Party).filter(Boolean))].sort();
          const electorates = [...new Set(data.hits.map(h => h.Electorate).filter(Boolean))].sort();
          const genders = [...new Set(data.hits.map(h => h.Gender).filter(Boolean))].sort();

          setAvailableOptions(prev => ({
            ...prev,
            names,
            parties,
            // Keep hardcoded states to ensure ACT and NT are always included
            states: ALL_STATES,
            electorates,
            genders: genders.length > 0 ? genders : ['Male', 'Female']
          }));
        }
      } catch (error) {
        console.error('Failed to fetch options:', error);
      }
    };
    fetchOptions();
  }, []);

  // Run search on initial load
  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (urlQuery && urlQuery !== '*') {
      handleSearch(urlQuery);
    } else {
      // Perform default search on load
      handleSearch('*');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field) => (event) => {
    setForm({ ...form, [field]: event.target.value });
  };

  const handleAutocompleteChange = (field) => (event, newValue) => {
    setForm({ ...form, [field]: newValue || '' });
  };

  // Debounced server-side name search
  const handleNameInputChange = (event, inputValue, reason) => {
    // Clear previous timeout
    if (nameSearchTimeoutRef.current) {
      clearTimeout(nameSearchTimeoutRef.current);
    }

    // Don't search on clear or reset
    if (reason === 'clear' || reason === 'reset' || !inputValue || inputValue.length < 2) {
      setNameSearchOptions([]);
      return;
    }

    // Debounce the search
    nameSearchTimeoutRef.current = setTimeout(async () => {
      setNameSearchLoading(true);
      try {
        // Search for names containing the input - search DisplayName, FamilyName, and GivenName
        const searchQuery = `DisplayName:*${inputValue}* OR FamilyName:*${inputValue}* OR GivenName:*${inputValue}*`;
        const data = await searchParliamentarians(searchQuery, 1, 50);
        if (data.hits) {
          const names = [...new Set(data.hits.map(h => h.DisplayName).filter(Boolean))].sort();
          setNameSearchOptions(names);
        }
      } catch (error) {
        console.error('Name search failed:', error);
        setNameSearchOptions([]);
      }
      setNameSearchLoading(false);
    }, 300); // 300ms debounce
  };

  const buildAdvancedQuery = () => {
    const parts = [];

    if (form.keywords) parts.push(`(${form.keywords})`);
    if (form.name) parts.push(`(DisplayName:"${form.name}" OR FamilyName:"${form.name}" OR GivenName:"${form.name}")`);
    if (form.party) parts.push(`Party:"${form.party}"`);
    if (form.state) parts.push(`State:"${form.state}"`);
    if (form.electorate) parts.push(`Electorate:"${form.electorate}"`);
    if (form.gender) parts.push(`Gender:"${form.gender}"`);
    if (form.chamber) parts.push(`MPorSenator:"${form.chamber}"`);
    if (form.inCurrentParliament !== 'all') {
      parts.push(`InCurrentParliament:"${form.inCurrentParliament}"`);
    }
    if (form.firstNations !== 'all') {
      parts.push(`FirstNations:${form.firstNations}`);
    }

    if (birthYearRange[0] > 1850 || birthYearRange[1] < currentYear) {
      parts.push(`DateOfBirth:[${birthYearRange[0]}-01-01 TO ${birthYearRange[1]}-12-31]`);
    }
    if (serviceYearRange[0] > 1901 || serviceYearRange[1] < currentYear) {
      parts.push(`ServiceHistory_Start:[${serviceYearRange[0]}-01-01 TO ${serviceYearRange[1]}-12-31]`);
    }

    return parts.length > 0 ? parts.join(' AND ') : '*';
  };

  const buildQueryWithFilters = (baseQuery, filters) => {
    const filterParts = [];
    Object.entries(filters).forEach(([field, values]) => {
      if (values.length > 0) {
        const fieldQuery = values.map(v => {
          // All fields need to be quoted, including InCurrentParliament which stores "True"/"False" as strings
          return `${field}:"${v}"`;
        }).join(' OR ');
        filterParts.push(`(${fieldQuery})`);
      }
    });

    if (filterParts.length === 0) return baseQuery;
    if (baseQuery === '*') return filterParts.join(' AND ');
    return `(${baseQuery}) AND ${filterParts.join(' AND ')}`;
  };

  const handleSearch = async (searchQuery = null, filters = activeFilters, page = 1, sort = sortOrder, pageSize = perPage) => {
    let baseQuery;
    if (searchMode === 'simple') {
      baseQuery = searchQuery !== null ? searchQuery : (query || '*');
    } else {
      baseQuery = buildAdvancedQuery();
    }

    setLoading(true);
    setError(null);
    try {
      const fullQuery = buildQueryWithFilters(baseQuery, filters);
      const data = await searchParliamentarians(fullQuery, page, pageSize, sort);
      setResults(data);
      setCurrentPage(page);
      // Use facets from server aggregations if available
      if (data.facets) {
        setComputedFacets(data.facets);
      }
      const newParams = { q: baseQuery, mode: searchMode };
      if (pageSize !== DEFAULT_PAGE_SIZE) newParams.pageSize = pageSize;
      setSearchParams(newParams);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search. Please try again.');
      setResults(null);
    }
    setLoading(false);
    setInitialLoading(false);
  };

  // Handle page change
  const handlePageChange = (event, page) => {
    handleSearch(null, activeFilters, page, sortOrder);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle sort change - reset to page 1 with new sort
  const handleSortChange = (newSort) => {
    setSortOrder(newSort);
    handleSearch(null, activeFilters, 1, newSort);
  };

  // Handle page size change - reset to page 1
  const handlePageSizeChange = (newSize) => {
    setPerPage(newSize);
    handleSearch(null, activeFilters, 1, sortOrder, newSize);
  };

  const handleReset = () => {
    setQuery('');
    setForm({
      keywords: '',
      name: '',
      party: '',
      state: '',
      electorate: '',
      gender: '',
      chamber: '',
      inCurrentParliament: 'all',
      firstNations: 'all'
    });
    setBirthYearRange([1850, currentYear]);
    setServiceYearRange([1901, currentYear]);
    setActiveFilters({ InCurrentParliament: ['True'] });
    setCurrentPage(1);
    setSortOrder('service_asc');
    setResults(null);
    setSearchParams({});
  };

  const FACET_ORDER = ['current', 'chambers', 'genders', 'states', 'parties', 'electorates', 'names', 'firstNations'];

  const facetToField = (facetName) => {
    const mapping = {
      'parties': 'Party',
      'states': 'State',
      'genders': 'Gender',
      'chambers': 'MPorSenator',
      'current': 'InCurrentParliament',
      'firstNations': 'FirstNations',
      'electorates': 'Electorate',
      'names': 'DisplayName'
    };
    return mapping[facetName] || facetName;
  };

  const facetLabel = (facetName) => {
    const labels = {
      'current': 'Current Parliament',
      'chambers': 'Chamber',
      'genders': 'Gender',
      'states': 'State',
      'parties': 'Party',
      'electorates': 'Electorate',
      'names': 'Name',
      'firstNations': 'First Nations'
    };
    return labels[facetName] || facetName.replace('_', ' ');
  };

  const handleFacetClick = (field, value) => {
    const newFilters = { ...activeFilters };
    if (!newFilters[field]) newFilters[field] = [];
    const valueIndex = newFilters[field].indexOf(value);
    if (valueIndex > -1) {
      newFilters[field] = newFilters[field].filter(v => v !== value);
      if (newFilters[field].length === 0) delete newFilters[field];
    } else {
      newFilters[field].push(value);
    }
    setActiveFilters(newFilters);
    handleSearch(null, newFilters, 1, sortOrder); // Reset to page 1 on filter change
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    handleSearch(null, {}, 1, sortOrder); // Reset to page 1 on clear
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setSearchMode(newMode);
      // Keep results when switching modes
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Parliamentarians</Typography>
        <ToggleButtonGroup
          value={searchMode}
          exclusive
          onChange={handleModeChange}
          size="small"
        >
          <ToggleButton value="simple">
            <SearchIcon sx={{ mr: 1 }} /> Simple
          </ToggleButton>
          <ToggleButton value="advanced">
            <TuneIcon sx={{ mr: 1 }} /> Advanced
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Simple Search */}
      <Collapse in={searchMode === 'simple'}>
        <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              placeholder="Search by name, party, electorate, occupation..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
            <Button
              variant="contained"
              onClick={() => handleSearch()}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ borderRadius: 3, px: 4 }}
            >
              Search
            </Button>
          </Box>
        </Paper>
      </Collapse>

      {/* Advanced Search */}
      <Collapse in={searchMode === 'advanced'}>
        <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Keywords"
                value={form.keywords}
                onChange={handleChange('keywords')}
                placeholder="Search across all fields"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={nameSearchOptions}
                value={form.name}
                onChange={handleAutocompleteChange('name')}
                onInputChange={handleNameInputChange}
                loading={nameSearchLoading}
                filterOptions={(x) => x} // Disable client-side filtering since server handles it
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Name"
                    placeholder="Type 2+ chars to search..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {nameSearchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                freeSolo
                noOptionsText={form.name?.length >= 2 ? "No names found" : "Type 2+ characters to search"}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.parties}
                value={form.party}
                onChange={handleAutocompleteChange('party')}
                renderInput={(params) => <TextField {...params} label="Party" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.states}
                value={form.state}
                onChange={handleAutocompleteChange('state')}
                renderInput={(params) => <TextField {...params} label="State" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.electorates}
                value={form.electorate}
                onChange={handleAutocompleteChange('electorate')}
                renderInput={(params) => <TextField {...params} label="Electorate" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Gender</InputLabel>
                <Select value={form.gender} onChange={handleChange('gender')} label="Gender">
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Chamber</InputLabel>
                <Select value={form.chamber} onChange={handleChange('chamber')} label="Chamber">
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="Member">Member (House)</MenuItem>
                  <MenuItem value="Senator">Senator</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Current Parliament</InputLabel>
                <Select value={form.inCurrentParliament} onChange={handleChange('inCurrentParliament')} label="Current Parliament">
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="True">Yes - Current</MenuItem>
                  <MenuItem value="False">No - Historical</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>First Nations</InputLabel>
                <Select value={form.firstNations} onChange={handleChange('firstNations')} label="First Nations">
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <YearRangeSlider
                label="Birth Year"
                value={birthYearRange}
                onChange={setBirthYearRange}
                min={1850}
                max={currentYear}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <YearRangeSlider
                label="Service Start Year"
                value={serviceYearRange}
                onChange={setServiceYearRange}
                min={1901}
                max={currentYear}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => handleSearch()}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Search
                </Button>
                <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 2 }}>
                  Reset
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  Query: <code>{buildAdvancedQuery() || '(empty)'}</code>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Loading skeleton for initial load */}
      {initialLoading && <SearchSkeleton count={5} showFacets={true} />}

      {/* Error state */}
      {!initialLoading && error && (
        <ErrorState
          message={error}
          onRetry={() => handleSearch()}
        />
      )}

      {/* Empty state when no results */}
      {!initialLoading && !error && results && results.total === 0 && (
        <EmptyState
          query={query}
          type="parliamentarians"
          onSuggestionClick={(q) => {
            setQuery(q);
            handleSearch(q);
          }}
          onReset={handleReset}
        />
      )}

      {/* Results display */}
      {!initialLoading && !error && results && results.total > 0 && (
        <Grid container spacing={3}>
          {/* Facets Panel */}
          {Object.keys(computedFacets).length > 0 && (
            <Grid item xs={12} md={3}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Filter Results
                  </Typography>
                  {Object.keys(activeFilters).length > 0 && (
                    <Button size="small" onClick={clearAllFilters}>Clear All</Button>
                  )}
                </Box>

                {FACET_ORDER.filter(facetName => computedFacets[facetName]?.length > 0).map((facetName) => {
                  const rawValues = computedFacets[facetName];
                  const facetValues = facetName === 'current'
                    ? [...rawValues].sort((a, b) => (a.value === 'True' ? -1 : b.value === 'True' ? 1 : 0))
                    : rawValues;
                  return (
                    <Accordion key={facetName} defaultExpanded={facetName === 'current'}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">
                          {facetLabel(facetName)}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <List dense sx={{ py: 0 }}>
                          {facetValues.slice(0, 10).map((facet) => (
                            <ListItem key={facet.value} disablePadding>
                              <Checkbox
                                size="small"
                                checked={activeFilters[facetToField(facetName)]?.includes(facet.value) || false}
                                onChange={() => handleFacetClick(facetToField(facetName), facet.value)}
                                inputProps={{
                                  'aria-label': `Filter by ${facetLabel(facetName)}: ${facetName === 'current' ? (facet.value === 'True' ? 'Yes' : 'No') : facet.value}`
                                }}
                              />
                              <ListItemText
                                primary={`${facetName === 'current' ? (facet.value === 'True' ? 'Yes' : 'No') : facet.value} (${facet.count})`}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Paper>
            </Grid>
          )}

          {/* Results */}
          <Grid item xs={12} md={Object.keys(computedFacets).length > 0 ? 9 : 12}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                <Typography variant="subtitle1">
                  {results.total} parliamentarians found
                  {results.total > perPage && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      (showing {((currentPage - 1) * perPage) + 1}-{Math.min(currentPage * perPage, results.total)})
                    </Typography>
                  )}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <PageSizeSelector value={perPage} onChange={handlePageSizeChange} />
                  <ExportCSVButton
                    dataType="parliamentarians"
                    query={query || '*'}
                    totalResults={results.total}
                  />
                  <SaveSearchButton
                    searchType="parliamentarians"
                    queryText={query || '*'}
                    parameters={{ sort: sortOrder, page: currentPage, per_page: perPage, ...activeFilters }}
                    resultCount={results.total}
                  />
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="sort-label">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <SortIcon fontSize="small" />
                        Sort by
                      </Box>
                    </InputLabel>
                    <Select
                      labelId="sort-label"
                      value={sortOrder}
                      onChange={(e) => handleSortChange(e.target.value)}
                      label="Sort by"
                    >
                      <MenuItem value="name_asc">Name (A-Z)</MenuItem>
                      <MenuItem value="name_desc">Name (Z-A)</MenuItem>
                      <MenuItem value="service_desc">Service Start (Newest)</MenuItem>
                      <MenuItem value="service_asc">Service Start (Oldest)</MenuItem>
                      <MenuItem value="party">Party</MenuItem>
                      <MenuItem value="state">State</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              <List>
                {results.hits.map((hit, index) => (
                  <React.Fragment key={hit.id}>
                    {index > 0 && <Divider />}
                    <ListItem alignItems="flex-start" sx={{ flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', width: '100%', alignItems: 'flex-start' }}>
                        <Avatar
                          src={getParliamentarianPhotoUrl(hit.PHID)}
                          alt={hit.DisplayName || `${hit.GivenName || ''} ${hit.FamilyName || ''}`.trim() || 'Parliamentarian'}
                          sx={{ mr: 2, bgcolor: 'primary.main', width: 48, height: 48 }}
                        >
                          {hit.GivenName?.[0]}{hit.FamilyName?.[0]}
                        </Avatar>
                        <ListItemText
                          primary={
                            <Typography
                              variant="resultTitle"
                              component={RouterLink}
                              to={`/parliamentarians/${hit.PHID}`}
                              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                              {formatParliamentarianName(hit)}
                            </Typography>
                          }
                          secondary={
                            <>
                              <Typography variant="body2" color="text.secondary">
                                {[
                                  hit.Party && `${hit.Party}${hit.PartyAbbrev ? ` (${hit.PartyAbbrev})` : ''}`,
                                  hit.State,
                                  hit.MPorSenator?.join(', ')
                                ].filter(Boolean).join(' · ')}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {[
                                  hit.ServiceHistory_Duration && `Service: ${hit.ServiceHistory_Duration}`,
                                  hit.DateOfBirth && `Born: ${formatDate(hit.DateOfBirth)}${hit.Age_String ? ` (${hit.Age_String})` : ''}`,
                                  !hit.DateOfBirth && hit.Age_String
                                ].filter(Boolean).join(' · ')}
                              </Typography>
                              {hit.Occupations && hit.Occupations.length > 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                  {hit.Occupations.slice(0, 2).join('; ')}
                                </Typography>
                              )}
                              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {hit.Electorate && (
                                  <Chip
                                    label={hit.Electorate}
                                    size="small"
                                    component={RouterLink}
                                    to={`/electorates?q=${encodeURIComponent(hit.Electorate)}`}
                                    clickable
                                  />
                                )}
                                {hit.InCurrentParliament === 'True' && (
                                  <Chip label="Current" color="success" size="small" />
                                )}
                                {hit.FirstNations && (
                                  <Chip label="First Nations" color="secondary" size="small" />
                                )}
                                {hit.Gender && (
                                  <Chip label={hit.Gender} size="small" variant="outlined" />
                                )}
                              </Box>
                            </>
                          }
                        />
                      </Box>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>

              {/* Pagination */}
              {results.total > perPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 1 }}>
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                    siblingCount={1}
                    boundaryCount={1}
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
